import { create } from "zustand";

type Theme = "dark" | "light";

interface ThemeState {
  theme: Theme;
  toggle: () => void;
  set: (theme: Theme) => void;
}

// App background per theme (--tr-bg values). The iOS standalone status bar is
// painted with the page's theme-color; keeping BOTH meta tags on the app's
// current background makes the bar blend seamlessly with the UI regardless of
// whether the SYSTEM scheme matches the app theme (the media-scoped metas are
// picked by the system scheme, so both must carry the app's colour).
const THEME_BG: Record<Theme, string> = {
  light: "#f2f2f4",
  dark: "#0a0a0c",
};

// Rebrand: the theme axis is driven by the `data-theme` attribute (key
// `trace-theme`). The `.dark` class is kept in sync as a bridge so legacy
// dark:* utilities keep working until the design-system migration completes.
function applyTheme(theme: Theme) {
  const el = document.documentElement;
  el.setAttribute("data-theme", theme);
  el.classList.toggle("dark", theme === "dark");
  localStorage.setItem("trace-theme", theme);
  localStorage.setItem("theme", theme); // legacy key, kept during migration
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = THEME_BG[theme];
    });
}

const stored = (localStorage.getItem("trace-theme") ??
  localStorage.getItem("theme")) as Theme | null;
// The product workspace is light-first. Existing explicit preferences are
// preserved; only users without a saved choice start in light mode.
const initial: Theme = stored === "dark" ? "dark" : "light";
applyTheme(initial);

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: initial,
  toggle: () => {
    const next = get().theme === "dark" ? "light" : "dark";
    applyTheme(next);
    set({ theme: next });
  },
  set: (theme: Theme) => {
    applyTheme(theme);
    set({ theme });
  },
}));
